import { useNotifications } from "@/lib/notifications";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { CheckCircle2, AlertTriangle, AlertCircle, Info, Bell, Trash2 } from "lucide-react";
import { tr, uiLocale } from "@/lib/i18n";

function iconFor(type) {
  if (type === "success") return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />;
  if (type === "error") return <AlertCircle className="w-3.5 h-3.5 text-red-600" />;
  if (type === "warning") return <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />;
  if (type === "info") return <Info className="w-3.5 h-3.5 text-blue-600" />;
  return <Bell className="w-3.5 h-3.5 text-[var(--ink-muted)]" />;
}

function timeAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return tr("{s}s ago", { s });
  if (s < 3600) return tr("{floor}m ago", { floor: Math.floor(s / 60) });
  if (s < 86400) return tr("{floor}h ago", { floor: Math.floor(s / 3600) });
  return new Date(ts).toLocaleString(uiLocale());
}

/**
 * Modal that shows the persistent notification log — everything the
 * toast system flashed at the editor since they logged in (bounded
 * to 100 rows in the provider). Only rendered when opened, so the
 * list is virtualization-free without hurting anything.
 */
export default function NotificationsCenter({ open, onOpenChange }) {
  const { items, clear } = useNotifications();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="notifications-center"
        className="max-w-lg max-h-[80vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <Bell className="w-5 h-5" />{tr("Notifications")}</DialogTitle>
          <DialogDescription className="text-xs text-[var(--ink-muted)]">{tr("Everything the app flashed at you recently. Toasts auto-dismiss; this is the log — capped at the last {value} entries.", { value: 100 })}</DialogDescription>
        </DialogHeader>

        {items.length === 0 ? (
          <div className="py-10 text-center text-sm text-[var(--ink-muted)]">{tr("No notifications yet.")}</div>
        ) : (
          <>
            <div className="flex justify-end">
              <button
                type="button"
                data-testid="notifications-clear"
                onClick={clear}
                className="inline-flex items-center gap-1.5 text-[11px] font-mono-display uppercase text-[var(--ink-muted)] hover:text-red-600"
              >
                <Trash2 className="w-3 h-3" />{tr("Clear all")}</button>
            </div>
            <ul className="space-y-1.5 mt-2">
              {items.map((n) => (
                <li
                  key={n.id}
                  data-testid={`notification-row-${n.id}`}
                  className="flex items-start gap-2 border border-[var(--border)] px-3 py-2 text-xs"
                >
                  <span className="mt-0.5 shrink-0">{iconFor(n.type)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[var(--ink)] leading-snug break-words">
                      {n.message}
                    </div>
                    <div className="text-[10px] font-mono-display text-[var(--ink-muted)] mt-0.5">
                      {timeAgo(n.ts)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
